<template>
  <div>
    <p class="image-label" v-html="model?.label"></p>
    <div
      :class="['case-line-image-input', `case-line-image-input--${size}`, hasValidationError ? 'error' : '']"
      v-on:click="!disabled && !loading ? onClick : null">
      <div v-if="loading" class="loader">
        <backstage-icon name="loading" />
      </div>

      <div v-else-if="model?.imageUrl" class="preview-image">
        <div class="image" :style="{ backgroundImage: `url(${model.imageUrl})` }"></div>
        <div v-if="!disabled" class="icon-cnt remove-icon" v-on:click="onClick">
          <backstage-icon class="icon-delete" :name="Icons.TRASH" />
        </div>
      </div>

      <div v-else class="preview-image">
        <div class="outer-border"></div>
        <div class="image" :style="{ backgroundImage: `url(${model?.backgroundImage})` }"></div>
        <template v-if="!disabled">
          <div class="icon-cnt add-icon">
            <backstage-icon class="plus-icon" :name="Icons.PLUS" />
          </div>
          <input :accept="accept" class="image-file-input" type="file" v-on:change="onChange" />
        </template>
        <div v-if="hasValidationError" class="image-error-overlay">
          <div class="image-error-inner"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Icons } from '@/models/icons.enum';
import { type CaseLineSlotImage } from '@/types/case.interface';
import { defineComponent, type PropType } from 'vue';
import BackstageIcon from '../../components/common/BackstageIcon.vue';

export default defineComponent({
  name: 'case-line-image-input',
  components: {
    'backstage-icon': BackstageIcon,
  },
  props: {
    model: {
      type: Object as PropType<CaseLineSlotImage>,
      default: undefined,
    },
    delete: {
      type: Function,
      default: undefined,
    },
    change: {
      type: Function,
      default: undefined,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    accept: {
      type: String,
      default: 'image/*',
    },
    // maximum file size in MB
    maxFileSize: {
      type: Number,
      default: 11,
    },
    // minimum file size in MB
    minFileSize: {
      type: Number,
      default: 0.1,
    },
    hasValidationError: {
      type: Boolean,
      default: false,
    },
    size: {
      type: String as PropType<'small' | 'large'>,
      default: 'large',
    },
  },
  data() {
    return {
      Icons,
      loading: false,
    };
  },
  methods: {
    onClick() {
      if (this.$props.model?.imageId && !this.loading && !this.disabled) {
        this.loading = true;
        (this.$props.delete as Function)(this.$props.model)?.then(() => (this.loading = false));
      }
    },
    onChange(e: Event) {
      const target = e.target as HTMLInputElement;
      const files = target.files as FileList;

      if (!files.length) {
        return;
      }

      const file = files[0] as File;

      const fileSize = file.size / 1048576;

      if (fileSize < this.minFileSize) {
        this.$snotify.error(this.$t('viewCase.file_size_too_small', { minFileSize: this.minFileSize }));

        return;
      }

      if (fileSize > this.maxFileSize) {
        this.$snotify.error(this.$t('viewCase.file_size_exceeded', { maxFileSize: this.maxFileSize }));

        return;
      }

      const fileReader = new FileReader();

      this.loading = true;

      fileReader.onerror = () => {
        this.loading = false;
      };

      fileReader.onload = () => {
        const data = fileReader.result;

        if (data) {
          (this.$props.change as Function)(file).then(() => (this.loading = false));
        }
      };

      fileReader.readAsDataURL(file);
    },
  },
});
</script>

<style lang="scss">
#backstage-ui {
  .image-error-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border-radius: 4px;
    background-color: rgba(244, 102, 102, 0.1);
    border: 2px solid rgba(244, 102, 102, 0.1);
    pointer-events: none;
  }

  .image-error-inner {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 98%;
    height: 98%;
    border: 1px dashed var(--red-color);
    pointer-events: none;
  }

  .case-line-image-input {
    position: relative;
    display: flex;
    border-radius: 4px;
    overflow: hidden;
    cursor: pointer;
    align-items: center;
    justify-content: center;
    background-color: var(--bg-page-color);

    .preview-image {
      width: 100%;
      height: 100%;
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .icon-cnt {
      position: absolute;
      margin: auto;
      height: 40%;
      width: 40%;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .add-icon {
      background-color: rgba(255, 255, 255, 0.7);
      z-index: 2;

      .icon-plus {
        background-size: 24px;
        background-color: var(--primary-text-color);
      }

      i {
        height: 60px;
        width: 60px;
      }
    }

    .remove-icon {
      display: flex;
      top: auto;
      bottom: 5px;
      right: 5px;
      left: auto;
      width: 24px;
      height: 24px;
      background-color: var(--primary-bg-color);
      opacity: 1;
      transition: background-color 0.2s ease;

      .icon-delete {
        height: 60%;
        width: 60%;
        background-size: 16px;
        filter: brightness(0);
      }
    }

    .small-icon {
      background: transparent;
    }

    .preview-image:hover .remove-icon {
      background-color: rgba(0, 0, 0, 0.3);
      opacity: 1;

      .icon-delete {
        filter: brightness(0) invert(1);
      }
    }

    .image-file-input {
      width: 100%;
      height: 100%;
      opacity: 0;
      cursor: pointer;
      z-index: 5;
    }
  }

  .image-label {
    font-size: 12px;
    color: var(--primary-text-color);
    margin-bottom: 10px;
  }

  .case-line-image-input--large {
    width: 150px;
    height: 150px;

    .preview-image {
      .outer-border {
        width: 100%;
        height: 100%;
        background-size: cover;
        background-position: center;
        position: absolute;
        left: 0;
        border: 1px solid var(--gray-color); /* Full solid outer border */
        border-radius: 4px;
        display: inline-block;
      }

      .image {
        width: 96%;
        height: 96%;
        background-size: cover;
        background-position: center;
        position: absolute;
        top: 2%;
        left: 2%;
        border: none;
      }
    }

    @media (max-width: 768px) {
      width: 100%;
      height: 180px;
    }

    &.error {
      .preview-image {
        .outer-border {
          border: 1px solid red;
        }
      }
    }
  }

  .case-line-image-input--small {
    width: 70px;
    height: 70px;

    @media (max-width: 768px) {
      width: 100%;
      max-width: 140px;
      height: 140px;
    }

    .preview-image {
      .outer-border {
        width: 100%;
        height: 100%;
        background-size: cover;
        background-position: center;
        position: absolute;
        left: 0;
        border: 1px solid var(--gray-color); /* Full solid outer border */
        border-radius: 4px;
        display: inline-block;
      }

      .image {
        width: 100%;
        height: 100%;
        background-size: cover;
        background-position: center;
        position: absolute;
        top: 0;
        left: 0;
        border: 1px solid var(--gray-color);
        border-radius: 4px;
      }
    }
  }
}
</style>
